import { ColDef, GetRowIdParams, RowClassParams } from "ag-grid-community";
import { OrbitalPassStep, WorkerRequest } from "./OrbitFeasibilityStudyWebWorkerTypes";
import { SIMULATION_TICK_SEC } from "./common/Orbital";
import { useRef, useEffect, useCallback } from "react";

export function getRowStyle( params: RowClassParams<OrbitalPassStep> ) {
    const range = params.data?.slantRange;
    if( range == null ) {
        return undefined;
    }
    const rowIndex = params.node.rowIndex;
    const gridApi = params.api;
    if( rowIndex !== null && gridApi ) {
        const prevRow = gridApi.getDisplayedRowAtIndex( rowIndex - 1 );
        const nextRow = gridApi.getDisplayedRowAtIndex( rowIndex + 1 );
        if( params.data?.tick! - prevRow?.data?.tick! !== ( SIMULATION_TICK_SEC * 1000 ) ) {
            return undefined;
        }
        const prevRange = prevRow?.data?.slantRange ?? Infinity;
        const nextRange = nextRow?.data?.slantRange ?? Infinity;                
        if( range < prevRange && range < nextRange ) {
            return { background: `rgba(173, 255, 173, 1)` }; //#77ff77
        }
    }
    return undefined;
}

interface HasType<T> {
    type: T;
}

export function useSimulationWorker<TIn extends HasType<any>, TOut extends HasType<any>>() {
    type  TInType  = TIn["type"];
    type  TOutType = TOut["type"];
    const workerRef    = useRef<Worker | null>( null );    
    const callbacksRef = useRef<Map<TOutType, ( data: any ) => void>>( new Map() );

    useEffect( () => {
        workerRef.current = new Worker(
            new URL( './OrbitFeasibilityStudyWebWorker.ts', import.meta.url ), { type: 'module' }
        );
        workerRef.current.addEventListener( 'message', ( event: MessageEvent<TOut> ) => {
            const type: TOut["type"] = event.data.type;
            const callback = callbacksRef.current.get( type );
            if( callback ) {
                callback( event.data );
            }
        } );
        return () => {
            workerRef.current?.terminate();
        };
    }, [] );
    const post = useCallback( ( message: WorkerRequest ) => {
        workerRef.current?.postMessage( message );
    }, [] );

    type RecvFunc = ( data: any ) => void;
    const recv = useCallback( ( type: TOutType, callback: RecvFunc ) => {
        callbacksRef.current.set( type, callback );
    }, [] );
    return { post, recv };
}